import { USER_REJECTED_TXN_ERR_MESSAGES } from './constants';
import { TransactionMessage, VersionedTransaction, } from '@solana/web3.js';
import { uniqueId } from 'lodash';
export const signAndSendTxns = async ({ txnsData, walletAndConnection, eventHandlers, options, }) => {
    const { connection, wallet } = walletAndConnection;
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
    const txns = (await Promise.all(txnsData.map((txnData) => createTxn({
        txnData,
        blockhash,
        walletAndConnection,
    })))).filter(Boolean);
    if (!wallet.signAllTransactions) {
        throw new Error("Wallet is not connected. Or doesn't support signAllTransactions method");
    }
    eventHandlers?.beforeApproveEveryChunk?.();
    const signedTxns = await wallet.signAllTransactions(txns);
    const txnHashes = await sendTransactions(signedTxns, walletAndConnection, options);
    const results = txnHashes.map((txnHash, idx) => ({
        txnHash,
        result: txnsData?.[idx]?.additionalResult,
    }));
    const confirmedResults = [];
    for (const result of results) {
        const confirmation = await connection.confirmTransaction({
            signature: result.txnHash,
            blockhash: blockhash,
            lastValidBlockHeight: lastValidBlockHeight,
        }, 'confirmed');
        if (!confirmation.value.err) {
            confirmedResults.push(result);
        }
        else {
            throw new Error('Transaction not confirmed');
        }
    }
    eventHandlers?.pfSuccessEach?.(confirmedResults);
    return confirmedResults;
};
const sendTransactions = async (txns, walletAndConnection, options) => {
    const { connection } = walletAndConnection;
    //? Create mock hashes if preventTxnsSending === true
    if (options.preventTxnsSending) {
        return txns.map(() => uniqueId('mockTxnHash_'));
    }
    const txnHashes = [];
    if (!options.parallelExecutionTimeot) {
        const hashes = await Promise.all(txns.map(async (txn) => await connection.sendRawTransaction(txn.serialize(), {
            skipPreflight: options.skipPreflight,
            preflightCommitment: options.preflightCommitment,
            maxRetries: options.maxRetries,
        })));
        txnHashes.push(...hashes);
    }
    else {
        for (let i = 0; i < txns.length; ++i) {
            const hash = await connection.sendRawTransaction(txns[i].serialize(), {
                skipPreflight: options.skipPreflight,
                preflightCommitment: options.preflightCommitment,
                maxRetries: options.maxRetries,
            });
            txnHashes.push(hash);
            await new Promise((resolve) => setTimeout(resolve, options.parallelExecutionTimeot));
        }
    }
    return txnHashes;
};
const createTxn = async ({ txnData, blockhash, walletAndConnection, }) => {
    const { connection, wallet } = walletAndConnection;
    const { lookupTables } = txnData;
    const lookupTableAccounts = await Promise.all(lookupTables.map((lt) => fetchLookupTableAccount(lt, connection)));
    const txnMessageV0 = new VersionedTransaction(new TransactionMessage({
        payerKey: wallet.publicKey,
        recentBlockhash: blockhash,
        instructions: txnData.instructions,
    }).compileToV0Message(lookupTableAccounts.map(({ value }) => value)));
    if (txnData.signers) {
        txnMessageV0.sign(txnData.signers);
    }
    return txnMessageV0;
};
const lookupTablesCache = new Map();
const fetchLookupTableAccount = (lookupTable, connection) => {
    const lookupTableAddressStr = lookupTable.toBase58();
    if (!lookupTablesCache.has(lookupTableAddressStr)) {
        const lookupTableAccountPromise = connection.getAddressLookupTable(lookupTable);
        lookupTablesCache.set(lookupTableAddressStr, lookupTableAccountPromise);
    }
    return lookupTablesCache.get(lookupTableAddressStr);
};
export const hasUserRejectedTxnApprove = (error) => {
    const { message } = error;
    if (USER_REJECTED_TXN_ERR_MESSAGES.includes(message)) {
        return true;
    }
    return false;
};
